import {DEFAULT_CURRENCY_CODE, Inject, Injectable} from '@angular/core';
import {Dictionary, keyBy, sumBy } from 'lodash';
import {AuthUserService} from '../auth/auth-user.service';
import {BackendService} from '../data/backend.service';
import {DataService} from '../data/data.service';
import {
  ProductReturnAttributesPayload,
  ProductReturnsPayload,
  ProductReturnsResult,
  ProductVariant,
  Settings, SPaymentAttributes,
  Transaction,
  TransactionHistoryItem
} from '../data/models';
import {SettingsDataService} from '../data/settings-data.service';
import * as uuid from 'uuid';
import {
  isSPaymentPOSTechnicalError,
  PaymentService as SPaymentService,
  SPaymentPOSTechnicalError
} from '../data/s-payment.service';
import { APP_SETTINGS } from '../data/settings-data.provider';
import { TransactionResult } from '../../plugins/s-payment-pos';
import { PaymentService } from '../data/payment.service';
import { PosApiPaymentService } from '../data/pos-api-payment.service';


export interface ProductReturnItemViewModel extends TransactionHistoryItem {
  returnQuantity: number;
  returnPrice: number;
  returnTaxAmount: number;
  productImageUrl: string;
  canEditReturnQuantity: boolean;
  canReturnItem: boolean;
}

function calculateReturnAmount(originalAmount: number, totalQuantity: number, returnQuantity: number): number {
  return -(originalAmount / totalQuantity * returnQuantity);
}

function increaseReturnQuantity(item: ProductReturnItemViewModel): ProductReturnItemViewModel {
  const reachedMax = item.returnQuantity + 1 >= item.quantity;
  const increasedQuantity = reachedMax ? item.quantity : item.returnQuantity + 1;

  return {
    ...item,
    returnQuantity: increasedQuantity,
    returnPrice: calculateReturnAmount(item.amount, item.quantity, increasedQuantity),
    returnTaxAmount: calculateReturnAmount(item.taxAmount, item.quantity, increasedQuantity)
  };
}

function decreaseReturnQuantity(item: ProductReturnItemViewModel): ProductReturnItemViewModel {
  const reachedMin = item.returnQuantity - 1 < 0;
  const decreasedQuantity = reachedMin ? 0 : item.returnQuantity - 1;

  return {
    ...item,
    returnQuantity: decreasedQuantity,
    returnPrice: calculateReturnAmount(item.amount, item.quantity, decreasedQuantity),
    returnTaxAmount: calculateReturnAmount(item.taxAmount, item.quantity, decreasedQuantity)
  };
}

@Injectable()
export class ProductReturnsService {
  transaction: Transaction | null = null;
  productReturnItems: ProductReturnItemViewModel[] = [];

  totalReturnPrice = 0;
  totalReturnTaxAmount = 0;
  totalReturnQuantity = 0;

  userAvatarUrl = '';

  productReturnsResult: ProductReturnsResult | null = null;

  hasProductsAvailableToReturn = false;
  totalQuantity = 0;
  isSPaymentAvailable = false;
  sPaymentTransactionId?: string;
  sPaymentTransactionData?: string;
  opiPaymentTransactionId?: string;
  zvtPaymentTransactionId?: string;

  private productVariantsMap: Dictionary<ProductVariant> = {};

  constructor(
    private readonly dataService: DataService,
    private readonly authService: AuthUserService,
    private readonly settingsService: SettingsDataService,
    private readonly backend: BackendService,
    private readonly sPaymentService: SPaymentService,
    private readonly paymentService: PaymentService,
    private readonly posApiPaymentService: PosApiPaymentService,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currency: string,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
  ) {}

  async initContent(id: number) {
    // Clear previous values on id change
    this.clear();

    // Fetch new data
    this.transaction = await this.dataService.fetchTransactionHistoryRecord(id);
    this.sPaymentTransactionId = this.transaction?.cartAttributes?.spos_payment_transaction_id;
    this.sPaymentTransactionData = this.transaction?.cartAttributes?.spos_payment_transaction_data;
    this.opiPaymentTransactionId = this.transaction?.cartAttributes?.opi_payment_transaction_id;
    this.zvtPaymentTransactionId = this.transaction?.cartAttributes?.zvt_payment_transaction_id;
    this.hasProductsAvailableToReturn = this.dataService.hasProductsAvailableToReturn(this.transaction);
    this.isSPaymentAvailable = (
      this.hasProductsAvailableToReturn
      && this.isSPaymentTransactionDataAvailable()
      && await this.sPaymentService.isSPaymentPOSAvailable()
    );
    this.setUserAvatar();
    await this.fetchProductVariantsMap();
    this.productReturnItems = this.transaction.items.map(item => {
      return {
        ...item,
        quantity: item.quantity - (item.returnedQuantity ?? 0),
        productImageUrl: this.productVariantsMap[item.productId]?.images[0]?.url || '',
        returnQuantity: 0,
        returnPrice: 0,
        returnTaxAmount: 0,
        canEditReturnQuantity: item.quantity > 1 && (item.returnedQuantity ?? 0) < item.quantity,
        canReturnItem: (item.returnedQuantity ?? 0) < 1,
      };
    });
    this.totalQuantity = sumBy(this.transaction.items.map(item => item.quantity));
  }

  private isSPaymentTransactionDataAvailable() {
    return !!(this.sPaymentTransactionId && this.sPaymentTransactionData);
  }

  async returnProducts() {
    this.recalculateSums();

    const itemsToReturn = this.productReturnItems
      .filter(item => item.returnQuantity)
      .map(item => {
        return {
          id: item.id,
          quantity: item.returnQuantity
        };
      });

    if (!this.transaction || !this.authService.authenticatedUser || !itemsToReturn.length) {
      return;
    }

    const returnItemsPayload: ProductReturnsPayload = {
      return: {
        transactionId: this.transaction.id,
        items: itemsToReturn,
        payment_type: 'versa_commerce_pos_cash'
      },
      userId: this.authService.authenticatedUser.id,
      posCustomer: this.dataService.posCustomer
    };
    this.productReturnsResult = await this.backend.returnProducts(returnItemsPayload);

    await this.dataService.refreshCashBox();
  }

  async returnProductsWithSPaymentPOS(): Promise<TransactionResult|undefined> {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_spos');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    if (!this.isSPaymentAvailable) {
      throw new Error('S-Payment POS app is not available!');
    }

    this.recalculateSums();

    const itemsToReturn = this.productReturnItems
      .filter(item => item.returnQuantity)
      .map(item => {
        return {
          id: item.id,
          quantity: item.returnQuantity
        };
      });

    if (!this.transaction || !this.authService.authenticatedUser || !itemsToReturn.length) {
      return;
    }

    const returnItemsPayload: ProductReturnsPayload = {
      return: {
        transactionId: this.transaction.id,
        items: itemsToReturn,
        payment_type: paymentMethod.gateway,
        // FIXME gabor 2023-08-28: always send credit card as long as the actual type can't be
        //   determined from the S-POS payment results
        card_type: 'cc',
      },
      userId: this.authService.authenticatedUser.id
    };
    await this.backend.validateReturnProducts(returnItemsPayload);
    try {
      const sPaymentTransactionId = uuid.v4();
      const transactionResult = await this.invokeSPaymentPOS(sPaymentTransactionId);
      if (!transactionResult.isAccepted) {
        // the case where the payment failed for non-technical reasons: the user cancelled the payment
        // or e.g. the PIN code was not accepted
        // we handle the case in the view component (showing the error message)
        return transactionResult;
      }
      await this.updateCartAttributes(this.transaction.id, sPaymentTransactionId, transactionResult);
      this.productReturnsResult = await this.backend.returnProducts(returnItemsPayload);
      this.productReturnsResult.isSPOS = true;
      // Theoretically unnecessary step, but just to make sure we have up-to-date information
      // in the cash box, for demo purposes. It only re-fetches the cash box state.
      await this.dataService.refreshCashBox();
      return transactionResult;
    } catch (error: any) {
      if (isSPaymentPOSTechnicalError(error)) {
        throw new SPaymentPOSTechnicalError(error.message, error.data);
      }
      throw error;
    }
  }

  async returnProductWithOPIPayment(): Promise<void> {

    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    this.recalculateSums();

    const itemsToReturn = this.productReturnItems
      .filter(item => item.returnQuantity)
      .map(item => {
        return {
          id: item.id,
          quantity: item.returnQuantity
        };
      });

    if (!this.transaction || !this.authService.authenticatedUser || !itemsToReturn.length) {
      return;
    }

    const returnItemsPayload: ProductReturnsPayload = {
      return: {
        transactionId: this.transaction.id,
        items: itemsToReturn,
        payment_type: paymentMethod.gateway,
        card_type: 'cc',
      },
      userId: this.authService.authenticatedUser.id
    };
    await this.backend.validateReturnProducts(returnItemsPayload);
    try {
      await this.invokeOPIPaymentReversal(this.opiPaymentTransactionId!);
      this.productReturnsResult = await this.backend.returnProducts(returnItemsPayload);
      // Theoretically unnecessary step, but just to make sure we have up-to-date information
      // in the cash box, for demo purposes. It only re-fetches the cash box state.
      await this.dataService.refreshCashBox();
    } catch (error: any) {
      throw error;
    }
  }

  private isReversalAllowed(): boolean {
    return this.transaction?.zOutStatus === 'open';
  }

  async returnProductWithPosApiPayment(): Promise<void> {

    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_zvt');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    this.recalculateSums();

    const itemsToReturn = this.productReturnItems
      .filter(item => item.returnQuantity)
      .map(item => {
        return {
          id: item.id,
          quantity: item.returnQuantity
        };
      });

    if (!this.transaction || !this.authService.authenticatedUser || !itemsToReturn.length) {
      return;
    }

    const returnItemsPayload: ProductReturnsPayload = {
      return: {
        transactionId: this.transaction.id,
        items: itemsToReturn,
        payment_type: paymentMethod.gateway,
        card_type: 'cc',
      },
      userId: this.authService.authenticatedUser.id
    };
    await this.backend.validateReturnProducts(returnItemsPayload);
    try {
      await this.invokePaymentApiPaymentReversal(this.zvtPaymentTransactionId!);
      this.productReturnsResult = await this.backend.returnProducts(returnItemsPayload);
      // Theoretically unnecessary step, but just to make sure we have up-to-date information
      // in the cash box, for demo purposes. It only re-fetches the cash box state.
      await this.dataService.refreshCashBox();
    } catch (error: any) {
      throw error;
    }
  }

  private async invokeSPaymentPOS(sPaymentTransactionId: string): Promise<TransactionResult> {
    if (this.isReversal()) {
      return await this.sPaymentService.executePaymentReversal(
        this.sPaymentTransactionId || '',
        this.totalReturnPrice,
        this.currency,
        this.sPaymentTransactionData || '',
      );
    }
    return await this.sPaymentService.executePaymentRefund(
      sPaymentTransactionId,
      this.totalReturnPrice,
      this.currency,
    );
  }

  private async invokeOPIPaymentReversal(opiPaymentTransactionId: string): Promise<void> {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');

    if (this.isReversal()) {
      return await this.paymentService.callRemoteTerminalReversePayment(
        opiPaymentTransactionId, paymentMethod?.settings?.terminalIp!,
      );
    }
    return await this.paymentService.callRemoteTerminalRefundPayment(
      opiPaymentTransactionId,
      paymentMethod?.settings?.terminalIp!,
      this.totalReturnPrice,
      this.currency,
    );
  }

  private async invokePaymentApiPaymentReversal(zvtPaymentTransactionId: string): Promise<void> {
    if (this.isReversal()) {
      await this.posApiPaymentService.callReversal(
        zvtPaymentTransactionId
      );
      return;
    }
    await this.posApiPaymentService.callRefund(
      zvtPaymentTransactionId,
      this.totalReturnPrice
    );
  }

  async checkTerminalStatus() {
      const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');
      return await this.paymentService.checkTerminalAvailable(paymentMethod?.settings?.terminalIp!);
  }

  private async updateCartAttributes(transaction_id: number, sPaymentTransactionId: string, transactionResult: TransactionResult) {
    let transactionAttributes: SPaymentAttributes;
    if (this.isReversal()) {
      transactionAttributes = {
        spos_reversal_transaction_id: sPaymentTransactionId,
        spos_reversal_transaction_data: transactionResult.transactionData,
        spos_reversal_customer_receipt: transactionResult.customerReceipt,
        spos_reversal_merchant_receipt: transactionResult.merchantReceipt,
        spos_reversal_card_circuit: transactionResult.cardCircuit,
      };
    }
    else {
      transactionAttributes = {
        spos_refund_transaction_id: sPaymentTransactionId,
        spos_refund_transaction_data: transactionResult.transactionData,
        spos_refund_customer_receipt: transactionResult.customerReceipt,
        spos_refund_merchant_receipt: transactionResult.merchantReceipt,
        spos_refund_card_circuit: transactionResult.cardCircuit,
      };
    }
    const payload: ProductReturnAttributesPayload = {
      attributes: transactionAttributes
    };
    await this.backend.updateReturnProductsAttributes(transaction_id, payload);
  }

  private isReversal() {
    return (this.totalReturnQuantity === this.totalQuantity) && this.isReversalAllowed();
  }

  increaseItemQuantity(itemId: number) {
    this.productReturnItems = this.productReturnItems.map(item => item.id !== itemId ? item : increaseReturnQuantity(item));
    this.recalculateSums();
  }

  decreaseItemQuantity(itemId: number) {
    this.productReturnItems = this.productReturnItems.map(item => item.id !== itemId ? item : decreaseReturnQuantity(item));
    this.recalculateSums();
  }

  /** Increase return quantity to max ordered items */
  selectItemForReturn(itemId: number) {
    this.productReturnItems = this.productReturnItems.map(item => {
      if (item.id !== itemId) {
        return item;
      }

      return {
        ...item,
        returnQuantity: item.quantity,
        returnPrice: -item.amount,
        returnTaxAmount: -item.taxAmount
      };
    });
    this.recalculateSums();
  }

  /** Reduce return quantity of item to 0, so item won't be returned */
  deselectItemFromReturn(itemId: number) {
    this.productReturnItems = this.productReturnItems.map(item => {
      if (item.id !== itemId) {
        return item;
      }

      return {
        ...item,
        returnQuantity: 0,
        returnPrice: 0,
        returnTaxAmount: 0
      };
    });
    this.recalculateSums();
  }

  clear() {
    this.productReturnsResult = null;
    this.transaction = null;
    this.productReturnItems = [];
    this.recalculateSums();
    this.setUserAvatar();
  }

  private recalculateSums() {
    this.totalReturnPrice = sumBy(this.productReturnItems, item => item.returnPrice);
    this.totalReturnTaxAmount = sumBy(this.productReturnItems, item => item.returnTaxAmount);
    this.totalReturnQuantity = sumBy(this.productReturnItems, item => item.returnQuantity);
  }

  private async fetchProductVariantsMap() {
    try {
      const products = await this.dataService.fetchAllProducts();
      const variants = products.flatMap(p => p.variants);
      this.productVariantsMap = keyBy(variants, v => v.id);
    } catch (error) {
      // Use products only to retrieve images
      console.error(error);
    }
  }

  private setUserAvatar() {
    if (!this.transaction || !this.settingsService.settings) {
      this.userAvatarUrl = '';
      return;
    }

    const user = this.settingsService.settings.users.find(user => user.name === this.transaction?.userName);
    if (!user) {
      this.userAvatarUrl = '';
      return;
    }

    this.userAvatarUrl = user.gravatarUrl;
  }

}
